/**
 * Foreground presence heartbeat.
 *
 * The server refreshes `users.last_online` on EVERY authenticated `/v1/api/*` request (see
 * App\Core\EndUserApiAuthController), so presence already tracks anyone actively moving around
 * the app. What it cannot see is someone sitting on a screen reading it: with no requests going
 * out, they fall past the 180s online window and their colleagues watch them go grey while they
 * are plainly there. This ping closes exactly that gap and nothing else.
 *
 * Two properties make it honest rather than just noisy:
 *
 *  - It fires ONLY while the document is visible. A background poller would keep a closed app
 *    reporting "online" indefinitely, which is worse than no presence at all — it would make the
 *    green dot mean "has the app installed" instead of "is here right now".
 *  - It is deliberately slower than the window it protects (90s against 180s). One dropped tick
 *    — a throttled timer, a brief offline blip — still lands inside the window, so presence does
 *    not flicker, while the request volume stays at two-thirds of a 60s cadence.
 *
 * Not merged into the token refresher's metronome: that ticks every 5 minutes (and only rotates
 * about every 15), which is far outside the online window, and it must keep running in the
 * background precisely so tokens survive — the opposite of what presence wants.
 */

import { presenceService } from '@/services/erpat';
import { hasAccessToken, getSessionEpoch } from '@/services/api/erpat-client';

/**
 * Ping interval. Must stay comfortably BELOW the server's online window
 * (presence_online_window_seconds() = 180s) so a single missed tick is survivable.
 */
const HEARTBEAT_INTERVAL_MS = 90 * 1000;

let timer: ReturnType<typeof setInterval> | null = null;
let inFlight = false;

/**
 * Send one heartbeat, unless there is nothing to report or a send is already in flight.
 *
 * Every failure is swallowed. A heartbeat is pure telemetry: a 401 is already handled by the
 * shared transport (which drives refresh / sign-out), and a network error simply means this tick
 * is lost — the next one is 90 seconds away and the user's own requests keep presence alive in
 * the meantime.
 */
async function beat(): Promise<void> {
  if (inFlight) return;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  if (!hasAccessToken()) return;

  // Capture the epoch BEFORE awaiting: if the user signs out mid-flight, the response must not
  // be treated as belonging to the session that started it.
  const epoch = getSessionEpoch();
  inFlight = true;
  try {
    await presenceService.ping();
  } catch {
    // Intentionally ignored — see the docblock.
  } finally {
    inFlight = false;
    if (epoch !== getSessionEpoch()) {
      // Signed out (or switched accounts) while this was in flight. Nothing to undo — the write
      // already happened server-side for the *previous* identity, which is accurate — but don't
      // let a stale completion keep a stopped heartbeat alive.
      return;
    }
  }
}

/** Ping immediately when the app comes back to the foreground, then resume the cadence. */
function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') void beat();
}

/**
 * Start the heartbeat. Idempotent — calling it while already running is a no-op.
 *
 * Sends one beat immediately so presence is correct the moment a session starts, rather than 90
 * seconds later.
 */
export function startPresenceHeartbeat(): void {
  if (timer !== null) return;
  timer = setInterval(() => void beat(), HEARTBEAT_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);
  void beat();
}

/** Stop the heartbeat and detach listeners. Idempotent. */
export function stopPresenceHeartbeat(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  document.removeEventListener('visibilitychange', onVisibilityChange);
}
